import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Grid
} from '@mui/material';
import logo from '../assets/gemistruct-logo.png';
import wordmark from '../assets/gemistruct-wordmark.png';
import sydneyBg from '../assets/sydney-background.jpg';

export default function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();

    try {
      setError('');
      setLoading(true);
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError('Failed to sign in: ' + err.message);
    }
    setLoading(false);
  }

  return (
    <Container maxWidth="xl" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
      <Grid container spacing={4}>
        {/* Left Side - Sign In Form */}
        <Grid item xs={12} md={6}>
          <Box sx={{ p: 4, maxWidth: 500, margin: 'auto' }}>
            <Box 
              component={Link}
              to="/"
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 6,
                textDecoration: 'none',
                '&:hover': { opacity: 0.9 }
              }}
            >
              <img src={logo} alt="Gemistruct Logo" style={{ height: 48, marginRight: 16 }} />
              <img src={wordmark} alt="Gemistruct" style={{ height: 36 }} />
            </Box>

            <Typography variant="h4" component="h1" gutterBottom fontWeight={700}>
              Welcome Back
            </Typography>
            <Typography color="text.secondary" gutterBottom>
              New to Gemistruct? <Link to="/signup" style={{ color: '#1976d2', textDecoration: 'none' }}>Create an account</Link>
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <form onSubmit={handleSubmit}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="Email Address"
                type="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="Password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 3, mb: 2, py: 1.5, fontSize: '1.1rem' }}
                disabled={loading}
              >
                Sign In
              </Button>

              <Box sx={{ textAlign: 'right', mb: 3 }}>
                <Link 
                  to="/reset-password" 
                  style={{ color: '#1976d2', textDecoration: 'none' }}
                >
                  Forgot Password?
                </Link>
              </Box>
            </form>
          </Box>
        </Grid>

        {/* Right Side - Marketing Content */}
        <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
          <Box
            sx={{
              height: '100%',
              bgcolor: 'transparent',
              backgroundImage: `linear-gradient(rgba(0, 47, 108, 0.4), rgba(0, 47, 108, 0.7)), url(${sydneyBg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: 2,
              p: 6,
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Typography variant="h2" gutterBottom fontWeight={700} sx={{ fontSize: { md: '3rem', lg: '3.75rem' } }}>
              Next-Gen Structural Engineering Software
            </Typography>
            <Typography variant="h5" sx={{ mb: 4, lineHeight: 1.5 }}>
              Lightning-fast, cloud-based design tools delivering instant results for beams, columns, footings, and connections—all compliant with Australian Standards.
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
} 